/**
 * "What it did" in the Health room, drawn: the tiles with a sparkline and the
 * change against the window before, the job and wait time distributions (as
 * Explore draws a histogram and a box), jobs per hour over wait p95 per hour,
 * worker time, and a row per worker with its box.
 * Sketch: `docs/design/sketches/argus-health-v2.html` (round 4, 2026-10-16).
 *
 * Everything added on 2026-10-16 is optional in the answer: an older backend
 * leaves out `dist`, `previous` and the new hourly and worker fields, and the
 * room then draws what it has without them.
 *
 * A mark that explains itself on hover carries `data-hl-tip`; the room shows
 * that text in one floating tip.
 */

import { html, nothing, svg, type SVGTemplateResult, type TemplateResult } from 'lit';
import type { ArgusHealthDist, ArgusHealthStats } from '../../api/types.ts';
import { change, duration, hoursPerBar } from './argus-health.ts';

export type DistForm = 'hist' | 'box' | 'both';

type Hour = ArgusHealthStats['hourly'][number];

/** The queues in their fixed order: the colour follows the queue, never its rank. */
const QUEUE_ORDER = ['s3', 'live', 'backtest'];

function num(n: number): string {
  return n.toLocaleString('en-GB');
}

function hhmm(iso: string): string {
  return new Date(iso).toISOString().slice(11, 16);
}

function queueClass(name: string): string {
  return QUEUE_ORDER.includes(name) ? `is-${name}` : 'is-other';
}

function byQueueOrder<T extends { name: string }>(rows: T[]): T[] {
  const rank = (n: string): number => (QUEUE_ORDER.includes(n) ? QUEUE_ORDER.indexOf(n) : QUEUE_ORDER.length);
  return [...rows].sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
}

/** "2.74 s" as a number and its unit, for a tile; "3 min 24 s" stays whole. */
function split(text: string): [string, string] {
  const m = /^([\d.]+) (s|h|d)$/.exec(text);
  return m ? [m[1]!, m[2]!] : [text, ''];
}

/** Hourly rows folded into buckets of `per` hours: counts add up, a time is the mean of the hours that have one. */
function buckets(rows: Hour[], per: number): Hour[][] {
  const out: Hour[][] = [];
  for (let i = 0; i < rows.length; i += per) out.push(rows.slice(i, i + per));
  return out;
}

function sumOf(rows: Hour[], f: (h: Hour) => number | null | undefined): number {
  return rows.reduce((n, h) => n + (f(h) ?? 0), 0);
}

function meanOf(rows: Hour[], f: (h: Hour) => number | null | undefined): number | null {
  const vs = rows.map(f).filter((v): v is number => v !== null && v !== undefined);
  return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null;
}

// ── tiles ──────────────────────────────────────────────────────────────────

function spark(values: (number | null)[]): TemplateResult | typeof nothing {
  const vs = values.map((v) => v ?? 0);
  if (vs.length < 2 || vs.every((v) => v === 0)) return nothing;
  const hi = Math.max(...vs);
  const lo = Math.min(0, ...vs);
  const y = (v: number): number => 24 - (22 * (v - lo)) / (hi - lo || 1);
  const x = (i: number): number => (100 * i) / (vs.length - 1);
  const d = vs.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join('');
  const last = vs[vs.length - 1]!;
  return html`<span class="argus-hl-spark" aria-hidden="true">
    <svg viewBox="0 0 100 26" preserveAspectRatio="none">
      <path class="is-area" d=${`${d}L100,26L0,26Z`}></path><path class="is-line" d=${d}></path>
    </svg>
    <i style=${`top:${((y(last) / 26) * 100).toFixed(1)}%`}></i>
  </span>`;
}

interface Tile {
  label: string;
  value: string;
  sub?: string | undefined;
  spark: (number | null)[] | null;
  now?: number | null | undefined;
  before?: number | null | undefined;
  /** True: up is good. False: up is bad. Null: neither, the change is only said. */
  upIsGood: boolean | null;
}

function renderChange(t: Tile, words: string): TemplateResult | typeof nothing {
  const c = change(t.now, t.before, t.upIsGood ?? true);
  if (!c) return nothing;
  const tone = t.upIsGood === null && c.tone !== 'flat' ? 'neutral' : c.tone;
  const title = `${c.pct > 0 ? 'up' : c.pct < 0 ? 'down' : 'about the same'} against the ${words} before`;
  const size = Math.abs(c.pct) > 999 ? '>999' : String(Math.abs(c.pct));
  return html`<span class="argus-hl-delta is-${tone}" title=${title}>${c.pct === 0 ? '± 0%' : `${c.pct > 0 ? '▲' : '▼'} ${size}%`}</span>`;
}

export function renderTiles(s: ArgusHealthStats, words: string): TemplateResult {
  const t = s.totals;
  const p = s.previous;
  const hourly = s.hourly;
  const has = (key: keyof Hour): boolean => hourly.some((h) => h[key] !== undefined);
  const per = hoursPerBar(hourly.length, 48);
  const bs = buckets(hourly, per);
  const count = (key: keyof Hour): (number | null)[] | null => (has(key) ? bs.map((b) => sumOf(b, (h) => h[key] as number)) : null);
  const time = (key: keyof Hour): (number | null)[] | null => (has(key) ? bs.map((b) => meanOf(b, (h) => h[key] as number | null)) : null);
  const groups: { kind: 'run' | 'wait' | 'plan'; label: string; tiles: Tile[] }[] = [
    { kind: 'run', label: 'Work done', tiles: [
      { label: 'Jobs done', value: num(t.scheduled_done), sub: t.scheduled_failed ? `${num(t.scheduled_failed)} failed` : 'none failed',
        spark: count('scheduled_done'), now: t.scheduled_done, before: p?.scheduled_done, upIsGood: true },
      { label: 'Rules run', value: num(t.rules), sub: 'one rule on one ship', spark: count('rules'), now: t.rules, before: p?.rules, upIsGood: true },
      { label: 'Episodes found', value: num(t.episodes), spark: count('episodes'), now: t.episodes, before: p?.episodes, upIsGood: null },
    ] },
    { kind: 'wait', label: 'Waiting and speed', tiles: [
      ...(t.wait_p50_s !== undefined
        ? [{ label: 'Wait p50', value: duration(t.wait_p50_s), sub: 'queued to claimed', spark: time('wait_p50_s'), now: t.wait_p50_s, before: p?.wait_p50_s, upIsGood: false }]
        : []),
      { label: 'Wait p95', value: duration(t.wait_p95_s), sub: t.wait_p50_s === undefined ? 'queued to claimed' : undefined,
        spark: time('wait_p95_s'), now: t.wait_p95_s, before: p?.wait_p95_s, upIsGood: false },
      ...(t.run_p50_s !== undefined
        ? [{ label: 'Job p50', value: duration(t.run_p50_s), sub: 'read and evaluate', spark: time('run_p50_s'), now: t.run_p50_s, before: p?.run_p50_s, upIsGood: false }]
        : []),
      { label: 'Job p95', value: duration(t.run_p95_s), sub: t.run_p99_s !== undefined && t.run_p99_s !== null ? `p99 ${duration(t.run_p99_s)}` : undefined,
        spark: time('run_p95_s'), now: t.run_p95_s, before: p?.run_p95_s, upIsGood: false },
    ] },
    { kind: 'plan', label: 'Other', tiles: [
      { label: 'Retried', value: num(t.retried), sub: '2nd or 3rd attempt', spark: count('retried'), now: t.retried, before: p?.retried, upIsGood: false },
      { label: 'Backtests', value: num(t.backtests_done), sub: t.backtests_failed ? `${num(t.backtests_failed)} failed` : 'none failed',
        spark: count('backtests_done'), now: t.backtests_done, before: p?.backtests_done, upIsGood: true },
    ] },
  ];
  return html`<div class="argus-hl-tgroups">
    ${groups.map((g) => html`<div class="argus-hl-tgroup is-${g.kind}">
      <span class="argus-hl-tgroup-l">${g.label}</span>
      <div class="argus-hl-tiles">
        ${g.tiles.map((tile) => {
          const [v, unit] = split(tile.value);
          return html`<div class="argus-hl-tile">
            <span class="argus-hl-tile-l">${tile.label}</span>
            <b>${v}${unit ? html`<small>${unit}</small>` : nothing}</b>
            <span class="argus-hl-tile-sub">${renderChange(tile, words)}${tile.sub ? html`<span>${tile.sub}</span>` : nothing}</span>
            ${tile.spark ? spark(tile.spark) : nothing}
          </div>`;
        })}
      </div>
    </div>`)}
  </div>`;
}

// ── distributions ──────────────────────────────────────────────────────────

/** Ticks on a log time axis, at least `gap` px apart. */
function logTicks(lo: number, hi: number, x: (v: number) => number, gap = 40): number[] {
  const all = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 60, 120, 300, 600, 1200, 1800, 3600, 7200];
  const out: number[] = [];
  for (const t of all) {
    if (t < lo || t > hi) continue;
    if (out.length && x(t) - x(out[out.length - 1]!) < gap) continue;
    out.push(t);
  }
  return out;
}

function tickLabel(t: number): string {
  if (t < 60) return `${t} s`;
  if (t < 3600) return `${t / 60} min`;
  return `${t / 3600} h`;
}

export function renderDist(title: string, note: string, d: ArgusHealthDist | undefined, form: DistForm,
  pick: (form: DistForm) => void): TemplateResult {
  const forms: [DistForm, string][] = [['hist', 'Histogram'], ['box', 'Box'], ['both', 'Both']];
  const head = html`<div class="argus-hl-card-h"><h4>${title}</h4><span class="argus-hl-note">${note}</span>
    <div class="date-presets argus-hl-form" role="group" aria-label=${`How ${title.toLowerCase()} is drawn`}>
      ${forms.map(([f, label]) => html`<button type="button" class="date-preset ${form === f ? 'active' : ''}"
        aria-pressed=${form === f ? 'true' : 'false'} @click=${() => pick(f)}>${label}</button>`)}
    </div></div>`;
  if (!d || !d.queues.length || d.edges.length < 2) {
    return html`<div class="argus-hl-card">${head}<p class="argus-lfaint">No job finished in this window.</p></div>`;
  }
  const queues = byQueueOrder(d.queues);
  const lo = d.edges[0]!;
  const hi = d.edges[d.edges.length - 1]!;
  const W = 620;
  const L = 62;
  const R = 26;
  const x = (v: number): number => L + ((W - L - R) * (Math.log(Math.max(v, lo)) - Math.log(lo))) / (Math.log(hi) - Math.log(lo) || 1);
  const ticks = logTicks(lo, hi, x);
  const showHist = form !== 'box';
  const showBox = form !== 'hist';
  const parts: SVGTemplateResult[] = [];
  let H = 0;
  if (showHist) {
    const top = 28;
    const bottom = 150;
    const max = Math.max(1, ...queues.flatMap((q) => q.counts));
    const y = (v: number): number => bottom - ((bottom - top) * v) / max;
    const fill = queues.length > 1 ? 'is-overlay' : 'is-single';
    parts.push(svg`<line class="argus-hl-grid" x1=${L} x2=${W - R} y1=${top} y2=${top}></line>
      <line class="argus-hl-grid" x1=${L} x2=${W - R} y1=${(top + bottom) / 2} y2=${(top + bottom) / 2}></line>
      <line class="argus-hl-axis" x1=${L} x2=${W - R} y1=${bottom} y2=${bottom}></line>
      <text class="argus-hl-tick" x=${L - 6} y=${top + 3} text-anchor="end">${num(max)}</text>
      <text class="argus-hl-tick" x=${L - 6} y=${bottom + 3} text-anchor="end">0</text>`);
    for (const q of queues) {
      q.counts.forEach((c, i) => {
        if (!c) return;
        const x0 = x(d.edges[i]!);
        const x1 = x(d.edges[i + 1]!);
        const w = Math.max(1, (x1 - x0) * 0.9);
        parts.push(svg`<rect class=${`argus-hl-bar ${queueClass(q.name)} ${fill}`} x=${x0 + (x1 - x0 - w) / 2} y=${y(c)} width=${w}
          height=${Math.max(0.5, bottom - y(c))} rx="1.5"
          data-hl-tip=${`${q.name}\n[${duration(d.edges[i])}, ${duration(d.edges[i + 1])})\n${num(c)} job${c === 1 ? '' : 's'}`}></rect>`);
      });
    }
    // p50, p95 and p99 of all queues together, labels on two rows so close ones do not overlap.
    ([['p50', d.all.p50], ['p95', d.all.p95], ['p99', d.all.p99]] as const).forEach(([k, v], i) => {
      if (v === null) return;
      const px = x(v);
      const ly = i % 2 ? top - 4 : top - 16;
      const end = px > W - 110;
      parts.push(svg`<line class="argus-hl-pmark" x1=${px} x2=${px} y1=${ly + 3} y2=${bottom}></line>
        <text class="argus-hl-plabel" x=${end ? px - 4 : px + 4} y=${ly} text-anchor=${end ? 'end' : 'start'}>${k} ${duration(v)}</text>`);
    });
    for (const t of ticks) parts.push(svg`<text class="argus-hl-tick" x=${x(t)} y=${bottom + 14} text-anchor="middle">${tickLabel(t)}</text>`);
    H = bottom + 20;
  }
  if (showBox) {
    const row = 26;
    const top = H + (showHist ? 8 : 4);
    queues.forEach((q, i) => {
      if (q.q1 === null || q.q3 === null || q.p50 === null) return;
      const cy = top + i * row + row / 2;
      const cls = queueClass(q.name);
      const wl = q.whisker_lo ?? q.min ?? q.q1;
      const wh = q.whisker_hi ?? q.max ?? q.q3;
      const tip = [`${q.name} · ${num(q.n)} jobs`, `min ${duration(q.min)} · q1 ${duration(q.q1)}`,
        `median ${duration(q.p50)} · mean ${duration(q.mean)}`, `q3 ${duration(q.q3)} · p95 ${duration(q.p95)}`,
        `max ${duration(q.max)} · ${num(q.outliers ?? 0)} outliers`].join('\n');
      parts.push(svg`<g class=${`argus-hl-box ${cls}`}>
        <text class="argus-hl-tick is-name" x=${L - 6} y=${cy + 3} text-anchor="end">${q.name}</text>
        <line x1=${x(wl)} x2=${x(wh)} y1=${cy} y2=${cy}></line>
        <line x1=${x(wl)} x2=${x(wl)} y1=${cy - 5} y2=${cy + 5}></line>
        <line x1=${x(wh)} x2=${x(wh)} y1=${cy - 5} y2=${cy + 5}></line>
        ${(q.outlier_values ?? []).map((v) => svg`<circle class="is-out" cx=${x(v)} cy=${cy} r="1.8"></circle>`)}
        <rect x=${x(q.q1)} y=${cy - 8} width=${Math.max(2, x(q.q3) - x(q.q1))} height="16" rx="2" data-hl-tip=${tip}></rect>
        <line class="is-median" x1=${x(q.p50)} x2=${x(q.p50)} y1=${cy - 8} y2=${cy + 8}></line>
        ${q.mean !== null ? svg`<circle class="is-mean" cx=${x(q.mean)} cy=${cy} r="2.6"></circle>` : nothing}
      </g>`);
    });
    const end = top + queues.length * row;
    if (!showHist) for (const t of ticks) parts.push(svg`<text class="argus-hl-tick" x=${x(t)} y=${end + 12} text-anchor="middle">${tickLabel(t)}</text>`);
    H = end + (showHist ? 4 : 18);
  }
  return html`<div class="argus-hl-card">
    ${head}
    <div class="argus-hl-key">
      ${queues.length > 1 ? queues.map((q) => html`<span><i class=${queueClass(q.name)}></i>${q.name}</span>`) : nothing}
      <span><i class="is-pmark"></i>p50 · p95 · p99${queues.length > 1 ? ', all queues' : ''}</span>
    </div>
    <svg class="argus-hl-chart" viewBox=${`0 0 ${W} ${H}`} role="img" aria-label=${`${title} per queue, on a log time axis`}>${parts}</svg>
    <div class="argus-ltable-wrap"><table class="argus-ltable argus-hl-table argus-hl-qstats">
      <thead><tr><th>Queue</th><th class="is-n">Jobs</th><th class="is-n">Mean</th><th class="is-n">p50</th><th class="is-n">p95</th><th class="is-n">p99</th><th class="is-n">Max</th></tr></thead>
      <tbody>${queues.map((q) => html`<tr>
        <td><i class=${`argus-hl-swatch ${queueClass(q.name)}`}></i>${q.name}</td><td class="is-n">${num(q.n)}</td>
        <td class="is-n">${duration(q.mean)}</td><td class="is-n"><b>${duration(q.p50)}</b></td>
        <td class="is-n">${duration(q.p95)}</td><td class="is-n">${duration(q.p99)}</td><td class="is-n">${duration(q.max)}</td>
      </tr>`)}</tbody>
    </table></div>
  </div>`;
}

// ── jobs per hour, and wait p95 per hour under it ─────────────────────────

export function renderHourly(s: ArgusHealthStats): TemplateResult {
  const rows = s.hourly;
  const perQueue = rows.some((r) => r.done_by_queue !== undefined);
  const per = hoursPerBar(rows.length, 96);
  const bs = buckets(rows, per);
  const W = 760;
  const L = 44;
  const R = 8;
  const top = 12;
  const bottom = 124;
  const top2 = 170;
  const bottom2 = 216;
  const H = 234;
  type Part = { name: string; cls: string; v: number };
  const stacks: Part[][] = bs.map((b) => {
    const failed = sumOf(b, (h) => h.scheduled_failed + h.backtests_failed);
    if (!perQueue) {
      return [
        { name: 'scheduled', cls: 'is-sched', v: sumOf(b, (h) => h.scheduled_done) },
        { name: 'backtests', cls: 'is-bt', v: sumOf(b, (h) => h.backtests_done) },
        { name: 'failed', cls: 'is-fail', v: failed },
      ];
    }
    const names = new Set(b.flatMap((h) => Object.keys(h.done_by_queue ?? {})));
    const done = byQueueOrder([...names].map((name) => ({ name }))).map(({ name }) => ({
      name, cls: queueClass(name), v: sumOf(b, (h) => h.done_by_queue?.[name]),
    }));
    return [...done, { name: 'failed', cls: 'is-fail', v: failed }];
  });
  const max = Math.max(1, ...stacks.map((st) => st.reduce((n, p) => n + p.v, 0)));
  const step = (W - L - R) / bs.length;
  const bar = Math.max(1, step * 0.76);
  const y = (v: number): number => bottom - ((bottom - top) * v) / max;
  const label = (b: Hour[]): string => (rows.length > 48 ? b[0]!.hour.slice(5, 10) : hhmm(b[0]!.hour));
  const every = Math.ceil(bs.length / 8);
  const parts: SVGTemplateResult[] = [svg`
    <line class="argus-hl-grid" x1=${L} x2=${W - R} y1=${top} y2=${top}></line>
    <line class="argus-hl-axis" x1=${L} x2=${W - R} y1=${bottom} y2=${bottom}></line>
    <text class="argus-hl-tick" x=${L - 6} y=${top + 4} text-anchor="end">${num(max)}</text>
    <text class="argus-hl-tick" x=${L - 6} y=${bottom + 4} text-anchor="end">0</text>`];
  stacks.forEach((st, i) => {
    const x = L + i * step + (step - bar) / 2;
    let base = 0;
    for (const p of st) {
      if (!p.v) continue;
      const y0 = y(base);
      base += p.v;
      // A 1 px gap of the card between stacked parts.
      parts.push(svg`<rect class=${`argus-hl-bar ${p.cls}`} x=${x} width=${bar} y=${y(base)} height=${Math.max(0.5, y0 - y(base) - (st.length > 1 ? 1 : 0))} rx="1"></rect>`);
    }
    const b = bs[i]!;
    const when = per > 1 ? `${label(b)} ${hhmm(b[0]!.hour)}, ${per} h` : rows.length > 48 ? `${label(b)} ${hhmm(b[0]!.hour)}` : label(b);
    const tip = [when, ...st.map((p) => `${p.name} ${num(p.v)}`)].join('\n');
    parts.push(svg`<rect class="argus-hl-hit" x=${L + i * step} y=${top} width=${step} height=${bottom - top} data-hl-tip=${tip}></rect>`);
    if (i % every === 0) parts.push(svg`<text class="argus-hl-tick" x=${x + bar / 2} y=${bottom + 14} text-anchor="middle">${label(b)}</text>`);
  });
  // Wait p95 per hour: its own chart on the same hours, never a second axis.
  const waits = rows.map((r) => r.wait_p95_s);
  const m2 = Math.max(1, ...waits.map((v) => v ?? 0));
  const y2 = (v: number): number => bottom2 - ((bottom2 - top2) * v) / m2;
  const xs = (i: number): number => L + ((i + 0.5) * (W - L - R)) / rows.length;
  let path = '';
  let open = false;
  waits.forEach((v, i) => {
    if (v === null) {
      open = false;
      return;
    }
    path += `${open ? 'L' : 'M'}${xs(i).toFixed(1)},${y2(v).toFixed(1)}`;
    open = true;
  });
  const lastAt = waits.map((v, i) => (v === null ? -1 : i)).reduce((a, b) => Math.max(a, b), -1);
  parts.push(svg`<text class="argus-hl-tick is-title" x=${L} y=${top2 - 10}>Wait p95 per hour</text>
    <line class="argus-hl-grid" x1=${L} x2=${W - R} y1=${top2} y2=${top2}></line>
    <line class="argus-hl-axis" x1=${L} x2=${W - R} y1=${bottom2} y2=${bottom2}></line>
    <text class="argus-hl-tick" x=${L - 6} y=${top2 + 4} text-anchor="end">${duration(m2)}</text>
    <text class="argus-hl-tick" x=${L - 6} y=${bottom2 + 4} text-anchor="end">0</text>
    ${path ? svg`<path class="argus-hl-wline" d=${path}></path>` : nothing}
    ${lastAt >= 0 ? svg`<circle class="argus-hl-wdot" cx=${xs(lastAt)} cy=${y2(waits[lastAt]!)} r="3.5"></circle>` : nothing}
    ${rows.map((r, i) => (r.wait_p95_s === null ? nothing : svg`<rect class="argus-hl-hit" x=${xs(i) - (W - L - R) / rows.length / 2} y=${top2}
      width=${(W - L - R) / rows.length} height=${bottom2 - top2} data-hl-tip=${`${rows.length > 48 ? `${r.hour.slice(5, 10)} ` : ''}${hhmm(r.hour)}\nwait p95 ${duration(r.wait_p95_s)}`}></rect>`))}`);
  const keyParts = perQueue
    ? byQueueOrder([...new Set(rows.flatMap((r) => Object.keys(r.done_by_queue ?? {})))].map((name) => ({ name })))
        .map(({ name }) => html`<span><i class=${queueClass(name)}></i>${name}</span>`)
    : [html`<span><i class="is-sched"></i>scheduled</span>`, html`<span><i class="is-bt"></i>backtests</span>`];
  return html`<div class="argus-hl-card">
    <div class="argus-hl-card-h"><h4>Jobs per hour${per > 1 ? html` <span class="argus-hl-note">· a bar is ${per} h</span>` : nothing}</h4>
      <div class="argus-hl-key">${keyParts}<span><i class="is-fail"></i>failed</span></div></div>
    <svg class="argus-hl-chart" viewBox=${`0 0 ${W} ${H}`} role="img" aria-label="Finished jobs per hour, and wait p95 per hour">${parts}</svg>
  </div>`;
}

// ── worker time ───────────────────────────────────────────────────────────

export function renderSplit(s: ArgusHealthStats): TemplateResult {
  const a = s.totals.busy_scheduled_s;
  const b = s.totals.busy_backtest_s;
  const all = a + b;
  const share = all ? Math.round((100 * a) / all) : 0;
  const windowS = s.hours * 3600;
  const pools = [...new Set(s.workers.map((w) => w.pool ?? '-'))].sort();
  return html`<div class="argus-hl-card">
    <div class="argus-hl-card-h"><h4>Worker time</h4><span>scheduled jobs against backtests</span></div>
    ${all
      ? html`<div class="argus-hl-split" role="img" aria-label=${`Scheduled ${share}%, backtests ${100 - share}%`}>
            <i class="is-sched" style=${`flex:${a}`}></i><i class="is-bt" style=${`flex:${b}`}></i>
          </div>
          <div class="argus-hl-key">
            <span><i class="is-sched"></i>scheduled ${share}% · ${duration(a)}</span>
            <span><i class="is-bt"></i>backtests ${100 - share}% · ${duration(b)}</span>
          </div>
          <div class="argus-ltable-wrap"><table class="argus-ltable argus-hl-table argus-hl-pooltable">
            <thead><tr><th>Pool</th><th class="is-n">Busy</th><th class="is-n">Of the window</th></tr></thead>
            <tbody>${pools.map((p) => {
              const ws = s.workers.filter((w) => (w.pool ?? '-') === p);
              const busy = ws.reduce((n, w) => n + w.busy_s, 0);
              const pct = Math.min(100, (100 * busy) / (windowS * Math.max(1, ws.length)));
              return html`<tr><td>${p === '-' ? 'no pool' : p} · ${ws.length} worker${ws.length === 1 ? '' : 's'}</td><td class="is-n">${duration(busy)}</td>
                <td class="is-n"><span class="argus-hl-meter"><i style=${`width:${pct}%`}></i></span>${pct.toFixed(1)}%</td></tr>`;
            })}</tbody>
          </table></div>`
      : html`<p class="argus-lfaint">No work finished in this window.</p>`}
  </div>`;
}

// ── per worker ─────────────────────────────────────────────────────────────

/** The median of the pool's workers' p50: the dashed line a worker's box is read against. */
export function poolMedians(workers: ArgusHealthStats['workers']): Map<string, number> {
  const out = new Map<string, number>();
  const pools = new Set(workers.map((w) => w.pool ?? '-'));
  for (const p of pools) {
    const p50s = workers.filter((w) => (w.pool ?? '-') === p && w.run_p50_s !== null).map((w) => w.run_p50_s!).sort((a, b) => a - b);
    if (p50s.length) out.set(p, p50s[Math.floor((p50s.length - 1) / 2)]!);
  }
  return out;
}

export function renderWorkerTable(s: ArgusHealthStats): TemplateResult {
  const windowS = s.hours * 3600;
  const withBox = s.workers.filter((w) => w.run_q1_s !== undefined && w.run_q1_s !== null && w.run_q3_s !== null);
  const medians = poolMedians(s.workers);
  const BW = 200;
  const lo = Math.max(0.01, Math.min(...withBox.map((w) => w.run_min_s ?? w.run_q1_s!)));
  const hi = Math.max(lo * 1.5, ...withBox.map((w) => w.run_p99_s ?? w.run_q3_s!));
  const bx = (v: number): number => 4 + ((BW - 8) * (Math.log(Math.min(Math.max(v, lo), hi)) - Math.log(lo))) / (Math.log(hi) - Math.log(lo));
  const ticks = withBox.length ? logTicks(lo, hi, bx, 34) : [];
  const box = (w: ArgusHealthStats['workers'][number]): TemplateResult | typeof nothing => {
    if (w.run_q1_s === undefined || w.run_q1_s === null || w.run_q3_s === null || w.run_q3_s === undefined || w.run_p50_s === null) return nothing;
    const median = medians.get(w.pool ?? '-');
    const cls = w.pool === 'backtest' ? 'is-backtest' : 'is-s3';
    return html`<svg class=${`argus-hl-rowbox ${cls}`} viewBox=${`0 0 ${BW} 18`} width=${BW} height="18" aria-hidden="true">
      ${median !== undefined ? svg`<line class="is-pool" x1=${bx(median)} x2=${bx(median)} y1="0" y2="18"></line>` : nothing}
      <line x1=${bx(w.run_min_s ?? w.run_q1_s)} x2=${bx(w.run_p99_s ?? w.run_q3_s)} y1="9" y2="9"></line>
      <rect x=${bx(w.run_q1_s)} y="3" width=${Math.max(2, bx(w.run_q3_s) - bx(w.run_q1_s))} height="12" rx="2"></rect>
      <line class="is-median" x1=${bx(w.run_p50_s)} x2=${bx(w.run_p50_s)} y1="3" y2="15"></line>
    </svg>`;
  };
  return html`<div class="argus-hl-card">
    <div class="argus-hl-card-h"><h4>Per worker</h4><span>a name is reused by the next task that takes it${withBox.length ? ' · the dashed line is the middle worker of the pool' : ''}</span></div>
    ${s.workers.length
      ? html`<div class="argus-ltable-wrap"><table class="argus-ltable argus-hl-table">
          <thead><tr><th>Worker</th><th>Pool</th><th class="is-n">Jobs</th><th class="is-n">Failed</th><th class="is-n">Rules</th>
            <th class="is-n">Episodes</th><th class="is-n">Job p50</th><th class="is-n">Job p95</th>
            ${withBox.length ? html`<th class="is-n">Job p99</th><th class="argus-hl-boxhead" style=${`width:${BW}px`}>
              <span>${ticks.map((t) => html`<i style=${`left:${bx(t)}px`}>${tickLabel(t).replace(' ', '')}</i>`)}</span></th>` : nothing}
            <th class="is-n">Backtests</th><th>Busy</th></tr></thead>
          <tbody>${s.workers.map((w) => {
            const busy = Math.min(100, (100 * w.busy_s) / windowS);
            const median = medians.get(w.pool ?? '-');
            const peers = s.workers.filter((x) => (x.pool ?? '-') === (w.pool ?? '-') && x.run_p50_s !== null).length;
            const slow = median !== undefined && peers > 1 && w.run_p50_s !== null && w.run_p50_s > 1.6 * median;
            const tip = w.run_q1_s !== undefined && w.run_q1_s !== null
              ? [`${w.name} · ${num(w.jobs_done + w.jobs_failed)} jobs`, `min ${duration(w.run_min_s)} · q1 ${duration(w.run_q1_s)}`,
                  `median ${duration(w.run_p50_s)} · q3 ${duration(w.run_q3_s)}`, `p95 ${duration(w.run_p95_s)} · p99 ${duration(w.run_p99_s)}`,
                  `max ${duration(w.run_max_s)}`].join('\n')
              : undefined;
            return html`<tr data-hl-tip=${tip ?? nothing}>
              <td><b>${w.name}</b>${slow ? html` <span class="argus-hl-bad argus-hl-slow">slower than its pool</span>` : nothing}</td><td>${w.pool ?? '-'}</td>
              <td class="is-n">${num(w.jobs_done)}</td>
              <td class="is-n ${w.jobs_failed ? 'argus-hl-bad' : ''}">${num(w.jobs_failed)}</td>
              <td class="is-n">${num(w.rules)}</td><td class="is-n">${num(w.episodes)}</td>
              <td class="is-n"><b>${duration(w.run_p50_s)}</b></td><td class="is-n">${duration(w.run_p95_s)}</td>
              ${withBox.length ? html`<td class="is-n">${duration(w.run_p99_s)}</td><td>${box(w)}</td>` : nothing}
              <td class="is-n">${num(w.backtests_done)}${w.backtests_failed ? html` <span class="argus-hl-bad">(${w.backtests_failed} failed)</span>` : nothing}</td>
              <td><span class="argus-hl-meter"><i style=${`width:${busy}%`}></i></span>${busy.toFixed(1)}% · ${duration(w.busy_s)}</td>
            </tr>`;
          })}</tbody></table></div>`
      : html`<p class="argus-lfaint">No worker finished a job in this window.</p>`}
  </div>`;
}
